'use client'

import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import type { LastPageTracked } from '@/lib/tracking/services/tracking.store'
import { lastPageTrackedAtom, trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { useApolloClient, useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { LoyaltyStatus, UserRole, UserTrackingDocument } from '@gql-generated/graphql'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import { type PageData, type TrackingUser } from '../services/tracking.eventTypes'
import { pushToGtmDataLayer } from '../services/tracking.gtm'

gql(/* GraphQL */ `
  query UserTracking {
    currentUserAccounts {
      id
    }
    currentUser {
      id
      role
      company {
        id
        type
        code
        professionCode
        pointOfSale {
          id
          parentId
          name
          subsidiary {
            id
            name
          }
        }
      }
      favoritePointOfSale {
        id
        parentId
      }
      checkout {
        cart {
          id
        }
      }
      loyalty {
        status
        data {
          active
        }
      }
    }
  }
`)

function mapCollaboratorType(role: UserRole | null | undefined): string {
  if (!role) return ''
  const roleMap: Record<UserRole, string> = {
    [UserRole.Admin]: 'administrateur',
    [UserRole.Manager]: 'administrateur',
    [UserRole.AdminDemo]: 'administrateur démo',
    [UserRole.Buyer]: 'acheteur',
    [UserRole.Guest]: 'visiteur',
    [UserRole.Demo]: 'démo',
  }

  return roleMap[role] ?? ''
}

function deriveAccountType(connected: boolean, companyType: string): string {
  if (!connected) return ''

  return companyType.toLowerCase() === 'tp' ? 'express' : 'classique'
}

function computeProgramFid(
  connected: boolean,
  status: LoyaltyStatus | undefined,
): {
  programFid: string
  programFidStatus: string
} {
  if (!connected || !status || status === LoyaltyStatus.Ineligible) {
    return { programFid: 'false', programFidStatus: '' }
  }

  return { programFid: 'true', programFidStatus: status }
}

function computeProfessionFromCompanyProfessionCode(
  connected: boolean,
  professionCode: string,
): { professionID: string; professionGroupID: string } {
  if (!connected) return { professionID: '', professionGroupID: '' }

  return {
    professionID: professionCode.match(/\d+/)?.[0] ?? '',
    professionGroupID: professionCode.match(/[A-Za-z]+/)?.[0] ?? '',
  }
}

/**
 * @private
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function useGetUserData(pageData: PageData | undefined): TrackingUser | undefined {
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const isAuthenticated = useIsAuthenticated()
  const { data, error, loading } = useQuery(UserTrackingDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    skip: !isTrackingEnabled || !isAuthenticated || isSSR,
  })

  if (!isTrackingEnabled) return

  if (loading) return

  if (error) return

  if (!pageData) return

  if (isAuthenticated && !data) return

  const currentUserData = data?.currentUser

  const isConnected = !!currentUserData?.id

  const loyaltyStatus = currentUserData?.loyalty?.status
  const { programFid, programFidStatus } = computeProgramFid(isConnected, loyaltyStatus)
  const { professionID, professionGroupID } = computeProfessionFromCompanyProfessionCode(
    isConnected,
    currentUserData?.company?.professionCode ?? '',
  )

  return {
    event: 'users-data',
    user: {
      userID: isConnected ? (currentUserData?.id ?? '') : '',
      clientID: isConnected ? (currentUserData?.company?.code ?? '') : '',
      cartID: isConnected ? (currentUserData?.checkout?.cart?.id ?? '') : '',
      cartCreationDate: '',
      cartLastUpdate: '',
      connexionStatus: isConnected ? 'connecte' : 'anonyme',
      subsidiaryID: isConnected
        ? (currentUserData?.company?.pointOfSale?.subsidiary?.id ?? '')
        : '',
      subsidiaryLabel: isConnected
        ? (currentUserData?.company?.pointOfSale?.subsidiary?.name ?? '')
        : '',
      agencyID: isConnected ? (currentUserData?.company?.pointOfSale?.id ?? '') : '',
      agencyLabel: isConnected ? (currentUserData?.company?.pointOfSale?.name ?? '') : '',
      userSessionID: isConnected ? 'DEPRECATED' : '', // TODO: Check with JG if this field is still needed
      professionID: isConnected ? professionID : '',
      professionGroupID: isConnected ? professionGroupID : '',
      quoteStatus: '',
      quotetype: '',
      userRoles: isConnected ? (currentUserData?.role ?? '') : '',
      programFid: isConnected ? programFid : '',
      programFidStatus: isConnected ? programFidStatus : '',
      collaboratorType: mapCollaboratorType(currentUserData?.role),
      accountType: deriveAccountType(isConnected, currentUserData?.company?.type ?? ''),
      magasinFavori:
        isConnected && currentUserData?.favoritePointOfSale
          ? currentUserData?.favoritePointOfSale?.id
          : '',
      magasinFavoriAntenne: isConnected
        ? currentUserData?.favoritePointOfSale?.parentId
          ? 'oui'
          : 'non'
        : '',
      isMulticompte: '',
      page: pageData,
    },
  }
}

export function useRefreshUserTrackingData() {
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const client = useApolloClient()

  const refreshUserTracking = useCallback(() => {
    if (!isTrackingEnabled) {
      return
    }

    client.refetchQueries({
      include: [UserTrackingDocument],
    })
  }, [client, isTrackingEnabled])

  return refreshUserTracking
}

const parsePageData = (pageData: PageData | undefined) => {
  if (!pageData) return undefined

  return {
    pageType: pageData.pageType,
    ...(pageData?.pageCategory &&
      pageData.pageCategory.length > 0 && { pageCategory: pageData.pageCategory }),
    ...(pageData?.pageSubCategory1 &&
      pageData.pageSubCategory1.length > 0 && { pageSubCategory1: pageData.pageSubCategory1 }),
    ...(pageData?.pageSubCategory2 &&
      pageData.pageSubCategory2.length > 0 && { pageSubCategory2: pageData.pageSubCategory2 }),
    ...(pageData?.pageSubCategory3 &&
      pageData.pageSubCategory3.length > 0 && { pageSubCategory3: pageData.pageSubCategory3 }),
  }
}

/**
 * Hook to track page views
 */
export function usePageTracking(pageData: PageData | undefined, productReference?: string) {
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const setLastPageTracked = useSetAtom(lastPageTrackedAtom)

  const userData = useGetUserData(parsePageData(pageData))

  const lastTrackedPageRef = useRef<LastPageTracked>({
    pageCategory: undefined,
    pageType: '',
    pageSubCategory1: undefined,
    pageSubCategory2: undefined,
    pageSubCategory3: undefined,
  })

  useEffect(() => {
    if (!isTrackingEnabled) {
      return
    }

    const isDifferentCategory = lastTrackedPageRef.current.pageCategory !== pageData?.pageCategory
    const isDifferentType = lastTrackedPageRef.current.pageType !== pageData?.pageType
    const isDifferentSubCategory1 =
      lastTrackedPageRef.current.pageSubCategory1 !== pageData?.pageSubCategory1
    const isDifferentSubCategory2 =
      lastTrackedPageRef.current.pageSubCategory2 !== pageData?.pageSubCategory2
    const isDifferentSubCategory3 =
      lastTrackedPageRef.current.pageSubCategory3 !== pageData?.pageSubCategory3
    const isDifferentPage =
      isDifferentCategory ||
      isDifferentType ||
      isDifferentSubCategory1 ||
      isDifferentSubCategory2 ||
      isDifferentSubCategory3

    if (userData && isDifferentPage) {
      pushToGtmDataLayer(userData)

      if (pageData?.pageCategory || productReference !== undefined) {
        const newPageData: LastPageTracked = {
          pageCategory: pageData?.pageCategory,
          pageType: pageData?.pageType ?? '',
          pageSubCategory1: pageData?.pageSubCategory1,
          pageSubCategory2: pageData?.pageSubCategory2,
          pageSubCategory3: pageData?.pageSubCategory3,
          productReference,
        }
        setLastPageTracked(newPageData)
        lastTrackedPageRef.current = newPageData
      }
    }
  }, [
    isTrackingEnabled,
    userData,
    setLastPageTracked,
    productReference,
    pageData?.pageType,
    pageData?.pageCategory,
    pageData?.pageSubCategory1,
    pageData?.pageSubCategory2,
    pageData?.pageSubCategory3,
  ])
}
